// Wrapper typed autour de worlds-metadata.json (audit upstream backend SSM).
//
// Source de verite pour les capacites par world : fleets, waypoints categorises, handlers
// delivery, zones cleaning, et flags de connectivite (lane_graph_indices) qui empechent les
// rejets silencieux du fleet_adapter "waypoint not in navigation graph".
//
// Schema v2.0.1 (bump 2026-05-27) : waypoints en dict (par name -> props) au lieu d'arrays
// categorises. Categorisation derivee des flags semantic (is_charger, is_cleaning_zone, etc.).
// Le wrapper reconstitue les arrays attendus par la UI pour eviter de toucher DispatchForm.
//
// Pour clean specifiquement, payload format compose nested (cf rmf-task-dispatch.md backend).
// Le frontend route automatiquement via real-backend-client.ts buildComposeCleanPayload.
//
// Sync : si bump upstream rmf_demos, le backend re-genere le JSON. Cote frontend :
//   cp ~/projects/rmf-demo/gitlab-profile/docs/data/worlds-metadata.json src/lib/rmf/

import raw from './worlds-metadata.json';
import type { TaskCategory } from '$lib/api/backend-client';
import type { MapName } from './maps';

// ===== Types runtime exposes =================================================

/** Info d'une fleet sur un world specifique (camelCase facade). */
export type WorldFleetInfo = {
  /** Categories supportees (deja resolu : intersection actions + task_capabilities). */
  readonly actions: readonly TaskCategory[];
  readonly robotsCount: number;
  readonly robotNames?: readonly string[];
  /** Zones nettoyables (sous-ensemble des cleaning_zones du world, depuis action_paths.clean). */
  readonly cleaningZones?: readonly string[];
  /**
   * Waypoints patrol-navigables pour cette fleet (depuis action_paths.patrol, livre backend
   * V2.3.16). Liste les waypoints reellement sur le nav_graph de la fleet, sans specialisation
   * (pas charger / pas cleaning_zone / pas pickup / pas dropoff).
   * undefined si le JSON est en version legacy (v2.0.x) sans ce champ : le frontend fallback
   * sur l'ensemble global du world (= comportement pre-Phase 8.49, dispatch peut echouer
   * "place not in graph" mais reste tolerant).
   */
  readonly patrolWaypoints?: readonly string[];
  /**
   * Waypoints delivery-navigables pour cette fleet (depuis action_paths.delivery, livre backend
   * V2.3.16). Liste les waypoints sur le nav_graph qui ont pickup_dispenser OU dropoff_ingestor.
   * Le frontend filtre ensuite pour separer pickup vs dropoff via le waypoint metadata du world.
   * undefined si JSON legacy : fallback sur tous les pickups/dropoffs du world.
   */
  readonly deliveryWaypoints?: readonly string[];
  /**
   * Indices des nav_graphs accessibles a cette fleet (option (a) backend V2.3.16). Utile pour
   * compositions sophistiquees (intersect avec waypoint.lane_graph_indices). Non utilise pour
   * MVP : on prefere consommer directement patrolWaypoints/deliveryWaypoints/cleaningZones
   * (option (b)/(c)/(d)) qui sont pre-calcules cote backend.
   */
  readonly laneGraphIndices?: readonly number[];
  readonly finishingRequest: 'park' | 'nothing' | 'charge';
};

export type WorldWaypoints = {
  /** Patrol-dispatchable : ni charger, ni cleaning_zone, lane_graph_indices ok. */
  readonly regular: readonly string[];
  /** Dispatchables pour clean (is_cleaning_zone, lane_graph_indices ok). */
  readonly cleaningZones: readonly string[];
  /** Chargers (a exclure du dispatch UI). */
  readonly chargers: readonly string[];
  /** Pickups (pickup_dispenser != null). */
  readonly pickups: readonly string[];
  /** Dropoffs (dropoff_ingestor != null). */
  readonly dropoffs: readonly string[];
  /** Chargers qui peuvent aussi servir de patrol cible (cas airport_terminal n03/s07/etc.). */
  readonly regularDualCharger: readonly string[];
};

export type WorldMetadata = {
  readonly fleets: Readonly<Record<string, WorldFleetInfo>>;
  readonly waypoints: WorldWaypoints;
  /** Mapping waypoint name -> dispenser handler (pour delivery pickup). */
  readonly dispensers: Readonly<Record<string, string>>;
  /** Mapping waypoint name -> ingestor handler (pour delivery dropoff). */
  readonly ingestors: Readonly<Record<string, string>>;
};

// ===== Schema raw du JSON (snake_case backend) ==============================

type RawWaypoint = {
  level: string;
  x: number;
  y: number;
  is_charger?: boolean;
  is_holding_point?: boolean;
  is_parking_spot?: boolean;
  is_cleaning_zone?: boolean;
  pickup_dispenser?: string;
  dropoff_ingestor?: string;
  spawn_robot_name?: string;
  spawn_robot_type?: string;
  /**
   * Indices des graphs (= fleets routing) connectes a ce waypoint via au moins une lane.
   * Liste vide [] = waypoint non-dispatchable (le fleet_adapter rejette "waypoint not in
   * navigation graph"). undefined = pas encore audite, on assume dispatchable (cf
   * audit_lane_pending top-level).
   */
  lane_graph_indices?: number[];
  _note?: string;
};

type RawFleet = {
  /** Liste des actions custom (ex `clean`, `teleop`). Standard `patrol`/`delivery` viennent
   *  de task_capabilities. */
  actions: string[];
  task_capabilities: { loop: boolean; delivery: boolean };
  finishing_request: string;
  recharge_threshold: number;
  max_delay: number | null;
  robots: Record<string, { charger: string }>;
  /** Mapping action_name -> liste de places. v2.1.0+ contient `patrol` (loop fleets), `delivery`
   *  (delivery fleets, intersection avec pickup_dispenser/dropoff_ingestor cote waypoint), et
   *  `clean` (cleaner fleets). v2.0.x avait seulement `clean`. */
  action_paths?: Record<string, string[]>;
  /** Indices des nav_graphs (= fleets routing) accessibles a cette fleet. Heuristique backend
   *  V2.3.16 : union des lane_graph_indices des chargers de tous les robots. */
  lane_graph_indices?: number[];
};

type RawWorld = {
  world: string;
  waypoints: Record<string, RawWaypoint>;
  fleets: Record<string, RawFleet>;
};

type RawRoot = {
  version: string;
  /** Si true, certains waypoints peuvent etre marques dispatchable mais rejetes par le
   *  fleet_adapter. Le frontend doit tolerer ces rejets gracefully (afficher l'erreur 400). */
  audit_lane_pending: boolean;
  worlds: Record<string, RawWorld>;
};

// ===== Reshape : dict waypoints -> arrays categorises ========================

/**
 * Detecte si un waypoint est non-dispatchable parce que pas connecte au nav_graph.
 * Convention : lane_graph_indices === undefined = pas audite (assume dispatchable),
 * lane_graph_indices === [] = audite vide (rejet garanti par fleet_adapter).
 */
function isLaneDispatchable(wp: RawWaypoint): boolean {
  if (wp.lane_graph_indices === undefined) return true;
  return wp.lane_graph_indices.length > 0;
}

function buildWaypoints(rawWaypoints: Record<string, RawWaypoint>): WorldWaypoints {
  const regular: string[] = [];
  const cleaningZones: string[] = [];
  const chargers: string[] = [];
  const pickups: string[] = [];
  const dropoffs: string[] = [];
  const regularDualCharger: string[] = [];

  for (const [name, wp] of Object.entries(rawWaypoints)) {
    if (!isLaneDispatchable(wp)) continue; // ex: hotel.kitchen en v2.0.1

    // Chargers : flag dedie. Note speciale : certains chargers (airport_terminal n03/s07/s11
    // etc.) sont ALSO regular patrol cibles d'apres la convention rmf_demos (dual-role :
    // spawn + charger + holding_point + is_parking_spot=true mais inclus dans les routes
    // patrol). On les detecte heuristique : charger AVEC spawn_robot_name = dual-role.
    if (wp.is_charger) {
      chargers.push(name);
      if (wp.spawn_robot_name) {
        regularDualCharger.push(name);
      }
      continue;
    }

    // Cleaning zones : separes des regular (pour clean dispatch uniquement).
    if (wp.is_cleaning_zone) {
      cleaningZones.push(name);
      continue;
    }

    // Tout le reste = regular (patrol + delivery cibles).
    regular.push(name);

    // Pickup / dropoff sont AUSSI dans regular (delivery via leur infra). Pas exclusifs.
    if (wp.pickup_dispenser) pickups.push(name);
    if (wp.dropoff_ingestor) dropoffs.push(name);
  }

  return { regular, cleaningZones, chargers, pickups, dropoffs, regularDualCharger };
}

function buildDispensersIngestors(rawWaypoints: Record<string, RawWaypoint>): {
  dispensers: Record<string, string>;
  ingestors: Record<string, string>;
} {
  const dispensers: Record<string, string> = {};
  const ingestors: Record<string, string> = {};
  for (const [name, wp] of Object.entries(rawWaypoints)) {
    if (!isLaneDispatchable(wp)) continue;
    if (wp.pickup_dispenser) dispensers[name] = wp.pickup_dispenser;
    if (wp.dropoff_ingestor) ingestors[name] = wp.dropoff_ingestor;
  }
  return { dispensers, ingestors };
}

function buildFleetInfo(fleet: RawFleet): WorldFleetInfo {
  // Resolution des actions : task_capabilities = standard rmf_demos (patrol=loop, delivery),
  // actions = custom (clean, teleop, ...). On expose en TaskCategory ce qu'on supporte cote UI.
  const cats = new Set<TaskCategory>();
  if (fleet.task_capabilities.loop) cats.add('patrol');
  if (fleet.task_capabilities.delivery) cats.add('delivery');
  if (fleet.actions.includes('clean')) cats.add('clean');
  // `teleop` est ignore (manipulation manuelle, hors UI dispatch).

  const robotNames = Object.keys(fleet.robots);
  // action_paths v2.1.0+ : patrol, delivery, clean (selon capacites). v2.0.x : seul clean
  // etait expose. Undefined = champ absent du JSON -> consommateurs frontend fallback sur
  // l'union globale du world (cf getPatrolWaypointsForFleetOnWorld).
  const cleaningZones = fleet.action_paths?.clean ?? undefined;
  const patrolWaypoints = fleet.action_paths?.patrol ?? undefined;
  const deliveryWaypoints = fleet.action_paths?.delivery ?? undefined;
  const finishingRequest =
    fleet.finishing_request === 'nothing' || fleet.finishing_request === 'charge'
      ? fleet.finishing_request
      : 'park';

  return {
    actions: Array.from(cats),
    robotsCount: robotNames.length,
    robotNames,
    cleaningZones,
    patrolWaypoints,
    deliveryWaypoints,
    laneGraphIndices: fleet.lane_graph_indices,
    finishingRequest
  };
}

function reshape(w: RawWorld): WorldMetadata {
  const waypoints = buildWaypoints(w.waypoints);
  const { dispensers, ingestors } = buildDispensersIngestors(w.waypoints);
  const fleets: Record<string, WorldFleetInfo> = {};
  for (const [name, raw_] of Object.entries(w.fleets)) {
    fleets[name] = buildFleetInfo(raw_);
  }
  return { fleets, waypoints, dispensers, ingestors };
}

const ROOT = raw as RawRoot;
const WORLDS: ReadonlyMap<string, WorldMetadata> = new Map(
  Object.entries(ROOT.worlds).map(([name, w]) => [name, reshape(w)])
);

// ===== API publique ==========================================================

/**
 * Si true, certains waypoints peuvent encore etre rejetes par le fleet_adapter meme s'ils
 * apparaissent comme dispatchables (audit lanes incomplete cote backend, cf v2.0.x). Le
 * frontend doit gracefully gerer les HTTP 400 sur dispatch et afficher le message d'erreur
 * sans crash. A false en v2.1.0.
 */
export const auditLanePending: boolean = ROOT.audit_lane_pending;

/** Retourne la metadata d'un world, ou null si placeholder/inconnu (campus, triple_H). */
export function getWorldMetadata(worldName: MapName | string): WorldMetadata | null {
  return WORLDS.get(worldName) ?? null;
}

/** Fleets reellement deployees sur ce world. */
export function getFleetsForWorld(worldName: MapName | string): readonly string[] {
  const meta = getWorldMetadata(worldName);
  return meta ? Object.keys(meta.fleets) : [];
}

/**
 * Categories dispatchables par une fleet sur un world donne, **filtrees par l'infra reellement
 * presente** :
 *   - `delivery` exige des dispensers ET ingestors non-vides
 *   - `clean` exige que la fleet ait cleaningZones non-vide
 *   - `patrol` n'a pas d'exigence
 *
 * Cf clinic.deliveryRobot : declare delivery dans task_capabilities mais le world n'a pas
 * de dispenser/ingestor -> on cache l'option pour eviter de proposer un dispatch qui finira
 * en "patrol etendu" sans cargo (confusing).
 */
export function getActionsForFleetOnWorld(
  worldName: MapName | string,
  fleetName: string
): readonly TaskCategory[] {
  const meta = getWorldMetadata(worldName);
  const fleetInfo = meta?.fleets[fleetName];
  if (!meta || !fleetInfo) return [];
  return fleetInfo.actions.filter((action) => {
    if (action === 'delivery') {
      return (
        Object.keys(meta.dispensers).length > 0 && Object.keys(meta.ingestors).length > 0
      );
    }
    if (action === 'clean') {
      return (fleetInfo.cleaningZones?.length ?? 0) > 0;
    }
    return true;
  });
}

/**
 * Union des categories supportees par TOUTES les fleets du world (mode auto-dispatch :
 * RMF Core arbitre, donc on accepte un payload compatible avec au moins UNE fleet).
 */
export function getAllCategoriesForWorld(
  worldName: MapName | string
): readonly TaskCategory[] {
  const fleets = getFleetsForWorld(worldName);
  const union = new Set<TaskCategory>();
  for (const fleet of fleets) {
    for (const action of getActionsForFleetOnWorld(worldName, fleet)) {
      union.add(action);
    }
  }
  return Array.from(union);
}

/**
 * Union des cleaning zones de TOUS les cleanerBots du world. Sur airport_terminal, cela
 * inclut zone_1..4 (cleanerBotA) meme si cleanerBotE ne peut pas faire zone_1. Le bidding
 * RMF Core assignera la task a la fleet qui peut effectivement la realiser.
 */
export function getAllCleaningZonesForWorld(
  worldName: MapName | string
): readonly string[] {
  const fleets = getFleetsForWorld(worldName);
  const union = new Set<string>();
  for (const fleet of fleets) {
    for (const zone of getCleaningZonesForFleetOnWorld(worldName, fleet)) {
      union.add(zone);
    }
  }
  return Array.from(union);
}

/** Zones de cleaning supportees par une fleet sur ce world. */
export function getCleaningZonesForFleetOnWorld(
  worldName: MapName | string,
  fleetName: string
): readonly string[] {
  return getWorldMetadata(worldName)?.fleets[fleetName]?.cleaningZones ?? [];
}

/** Waypoints "regular" + dual chargers. Filtres par lane_graph_indices cote reshape. */
export function getPatrolWaypointsForWorld(worldName: MapName | string): readonly string[] {
  const meta = getWorldMetadata(worldName);
  if (!meta) return [];
  return [...meta.waypoints.regular, ...meta.waypoints.regularDualCharger];
}

/**
 * Waypoints patrol-navigables par une fleet specifique. Source : `action_paths.patrol` du
 * worlds-metadata.json v2.1.0+ (backend V2.3.16). Si le champ est absent (JSON legacy v2.0.x),
 * fallback sur l'union globale du world — comportement degrade mais retrocompatible. Pour
 * mode AUTO (fleet null), retourne l'union (RMF Core arbitrera).
 */
export function getPatrolWaypointsForFleetOnWorld(
  worldName: MapName | string,
  fleetName: string
): readonly string[] {
  const meta = getWorldMetadata(worldName);
  const fleetInfo = meta?.fleets[fleetName];
  // Priorite : liste fleet-specifique du backend si dispo. Sinon fallback union (= pre-V2.1.0).
  return fleetInfo?.patrolWaypoints ?? getPatrolWaypointsForWorld(worldName);
}

/** Waypoints pickup + handler. Vide si pas de delivery infra. */
export function getPickupOptionsForWorld(
  worldName: MapName | string
): readonly { place: string; handler: string }[] {
  const meta = getWorldMetadata(worldName);
  if (!meta) return [];
  return meta.waypoints.pickups
    .map((place) => ({ place, handler: meta.dispensers[place] }))
    .filter((opt): opt is { place: string; handler: string } => Boolean(opt.handler));
}

/**
 * Pickups accessibles par une fleet specifique : intersection des pickups globaux du world
 * avec `action_paths.delivery` de la fleet (livre backend V2.3.16). Si action_paths.delivery
 * est absent (JSON legacy), fallback sur l'union globale (= comportement pre-V2.1.0).
 */
export function getPickupOptionsForFleetOnWorld(
  worldName: MapName | string,
  fleetName: string
): readonly { place: string; handler: string }[] {
  const meta = getWorldMetadata(worldName);
  const fleetInfo = meta?.fleets[fleetName];
  const allPickups = getPickupOptionsForWorld(worldName);
  // Pas de filtrage si la fleet n'expose pas action_paths.delivery : on retombe sur tout
  // pour eviter de cacher des pickups potentiellement valides en mode legacy.
  if (!fleetInfo?.deliveryWaypoints) return allPickups;
  const allowed = new Set(fleetInfo.deliveryWaypoints);
  return allPickups.filter((opt) => allowed.has(opt.place));
}

/** Waypoints dropoff + handler. Vide si pas de delivery infra. */
export function getDropoffOptionsForWorld(
  worldName: MapName | string
): readonly { place: string; handler: string }[] {
  const meta = getWorldMetadata(worldName);
  if (!meta) return [];
  return meta.waypoints.dropoffs
    .map((place) => ({ place, handler: meta.ingestors[place] }))
    .filter((opt): opt is { place: string; handler: string } => Boolean(opt.handler));
}

/**
 * Dropoffs accessibles par une fleet specifique : intersection des dropoffs globaux du world
 * avec `action_paths.delivery` de la fleet. Meme logique que getPickupOptionsForFleetOnWorld.
 */
export function getDropoffOptionsForFleetOnWorld(
  worldName: MapName | string,
  fleetName: string
): readonly { place: string; handler: string }[] {
  const meta = getWorldMetadata(worldName);
  const fleetInfo = meta?.fleets[fleetName];
  const allDropoffs = getDropoffOptionsForWorld(worldName);
  if (!fleetInfo?.deliveryWaypoints) return allDropoffs;
  const allowed = new Set(fleetInfo.deliveryWaypoints);
  return allDropoffs.filter((opt) => allowed.has(opt.place));
}

/** True si le waypoint joue aussi role charger (info UX pour le picker patrol). */
export function isWaypointDualCharger(
  worldName: MapName | string,
  waypointName: string
): boolean {
  const dual = getWorldMetadata(worldName)?.waypoints.regularDualCharger ?? [];
  return dual.includes(waypointName);
}
